import React, { useEffect, useState, createContext, useContext } from 'react';
interface PricingConfig {
  currentPrice: number;
  comparePrice: number;
  currencyCode: string;
  locale: string;
  decimals: number;
  symbol: string;
}
interface PricingContextValue {
  country: string;
  currentPrice: number;
  comparePrice: number;
  currencyCode: string;
  formatPrice: (amount: number) => string;
  formatBonusPrice: (usdAmount: number) => string;
  bonusTotal: string;
  isLoading: boolean;
}
const PRICING_CONFIG: Record<string, PricingConfig> = {
  CL: {
    currentPrice: 13999,
    comparePrice: 44999,
    currencyCode: 'CLP',
    locale: 'es-CL',
    decimals: 0,
    symbol: '$'
  },
  AR: {
    currentPrice: 21999,
    comparePrice: 71999,
    currencyCode: 'ARS',
    locale: 'es-AR',
    decimals: 0,
    symbol: '$'
  },
  CO: {
    currentPrice: 58999,
    comparePrice: 191999,
    currencyCode: 'COP',
    locale: 'es-CO',
    decimals: 0,
    symbol: '$'
  },
  MX: {
    currentPrice: 269,
    comparePrice: 899,
    currencyCode: 'MXN',
    locale: 'es-MX',
    decimals: 0,
    symbol: '$'
  },
  UY: {
    currentPrice: 599,
    comparePrice: 1999,
    currencyCode: 'UYU',
    locale: 'es-UY',
    decimals: 0,
    symbol: '$'
  },
  ES: {
    currentPrice: 12.99,
    comparePrice: 42.99,
    currencyCode: 'EUR',
    locale: 'es-ES',
    decimals: 2,
    symbol: '€'
  },
  PE: {
    currentPrice: 52.99,
    comparePrice: 171.99,
    currencyCode: 'PEN',
    locale: 'es-PE',
    decimals: 2,
    symbol: 'S/'
  },
  US: {
    currentPrice: 14.99,
    comparePrice: 49.99,
    currencyCode: 'USD',
    locale: 'en-US',
    decimals: 2,
    symbol: '$'
  }
};
const DEFAULT_CONFIG = PRICING_CONFIG.US;
const PricingContext = createContext<PricingContextValue | undefined>(undefined);
function formatNumber(amount: number, config: PricingConfig): string {
  const {
    decimals,
    locale,
    currencyCode,
    symbol
  } = config;
  // Format the number based on locale
  if (decimals === 0) {
    // For currencies with no decimals (CLP, ARS, COP, MXN, UYU)
    // Use locale-specific thousands separator
    const formatted = Math.round(amount).toLocaleString(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return `${symbol}${formatted} ${currencyCode}`;
  } else {
    // For currencies with decimals (EUR, PEN, USD)
    const formatted = amount.toLocaleString(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
    return `${symbol}${formatted} ${currencyCode}`;
  }
}
function scaleBonusPrice(usdAmount: number, config: PricingConfig): number {
  // Scale USD bonus amounts to local currency using the ratio of compare prices
  const ratio = config.comparePrice / DEFAULT_CONFIG.comparePrice;
  return usdAmount * ratio;
}
export function PricingProvider({
  children
}: {
  children: ReactNode;
}) {
  const [country, setCountry] = useState<string>('US');
  const [isLoading, setIsLoading] = useState(true);
  const [config, setConfig] = useState<PricingConfig>(DEFAULT_CONFIG);
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        if (response.ok) {
          const data = await response.json();
          const detectedCountry = data.country_code || 'US';
          setCountry(detectedCountry);
          setConfig(PRICING_CONFIG[detectedCountry] || DEFAULT_CONFIG);
        }
      } catch (error) {
        // Fallback to USD on error
        console.warn('Geolocation failed, defaulting to USD');
        setCountry('US');
        setConfig(DEFAULT_CONFIG);
      } finally {
        setIsLoading(false);
      }
    };
    detectCountry();
  }, []);
  const formatPrice = (amount: number): string => {
    return formatNumber(amount, config);
  };
  const formatBonusPrice = (usdAmount: number): string => {
    const scaledAmount = scaleBonusPrice(usdAmount, config);
    return formatNumber(scaledAmount, config);
  };
  const bonusTotal = formatNumber(config.comparePrice, config);
  const value: PricingContextValue = {
    country,
    currentPrice: config.currentPrice,
    comparePrice: config.comparePrice,
    currencyCode: config.currencyCode,
    formatPrice,
    formatBonusPrice,
    bonusTotal,
    isLoading
  };
  return <PricingContext.Provider value={value} data-id="element-442">{children}</PricingContext.Provider>;
}
export function usePricing(): PricingContextValue {
  const context = useContext(PricingContext);
  if (context === undefined) {
    throw new Error('usePricing must be used within a PricingProvider');
  }
  return context;
}